import React, { useState, FocusEvent } from "react"
import { AnimatedInputLabel } from "../../common/general-styles.component"
import { noop, uniqueId } from "lodash"
import { TextareaWithErrorProps } from "./textarea.interface"
import styled from "styled-components"
import { AppFunctionComponent } from "../../../types"
import useTranslationHook from "../../../hooks/use-translation.hook"

const TextareaWrapper = styled.div`
  position: relative;
`

const AnimatedTextareaLabel = styled(AnimatedInputLabel)`
  top: ${({ focused, empty }) =>
    focused || !empty ? `0` : `calc(25% - 1rem)`};
  font-size: ${({ focused, empty }) =>
    focused || !empty ? `${9 / 16}rem` : `${12 / 16}rem`};
  line-height: ${({ focused, empty }) =>
    focused || !empty ? `${16 / 16}rem` : `${25 / 16}rem`};
`

const TextareaElement = styled.textarea<{ error?: boolean }>`
  width: 100%;
  z-index: ${({ theme }) => theme.zIndexes.midLayer};
  border: none;
  height: 130px;
  min-height: 64px;
  outline: none;
  padding: 15px 30px;
  appearance: none;
  position: relative;
  box-sizing: border-box;
  font-size: ${14 / 16}rem;
  font-weight: 300;
  line-height: 30px;
  border-radius: ${({ theme }) => theme.size.borderRadius};
  background-color: ${({ theme }) => theme.color.backgroundInverted};

  border: 1px solid
    ${({ theme, error }) =>
      error ? theme.color.error : theme.color.commerceBorder};

  @media (min-width: ${({ theme }) => theme.breakpoint.mobileHorizontal}px) {
    font-size: 1rem;
  }
  &::placeholder {
    color: transparent;
  }
`

const Textarea: AppFunctionComponent<TextareaWithErrorProps> = ({
  className,
  placeholder,
  defaultValue,
  required,
  error,
  onBlur = noop,
  onFocus = noop,
  inputRef,
  name,
  ...props
}) => {
  const [focused, setFocus] = useState(false)
  const { translate } = useTranslationHook()

  const focus = (event: FocusEvent<HTMLTextAreaElement>) => {
    setFocus(true)
    onFocus(event)
  }

  const blur = (event: FocusEvent<HTMLTextAreaElement>) => {
    setFocus(Boolean(props.value))
    onBlur(event)
  }

  const id = `id-${uniqueId()}`

  return (
    <TextareaWrapper className={className}>
      <AnimatedTextareaLabel
        focused={focused}
        empty={!defaultValue && !props.value}
        htmlFor={id}
      >
        {placeholder} {!required && `(${translate("Optional")})`}
      </AnimatedTextareaLabel>
      <TextareaElement
        name={name}
        placeholder={placeholder}
        defaultValue={defaultValue}
        error={Boolean(error)}
        onFocus={focus}
        onBlur={blur}
        id={id}
        {...props}
        ref={inputRef}
      />
    </TextareaWrapper>
  )
}

export default Textarea
